import type { CraftableWeapon, PlannerMode } from "@/lib/crafting/types";
import { ammoLabel, assetUrl } from "@/lib/bungie/assets";

export interface WeaponIdentityPanelProps {
  weapon: CraftableWeapon;
  mode: PlannerMode;
}

/** The top-left identity block: icon, eyebrow, name, type line, flavor text. */
export function WeaponIdentityPanel({ weapon, mode }: WeaponIdentityPanelProps) {
  const icon = assetUrl(weapon.icon);
  const element = assetUrl(weapon.damageTypeIcon);
  const ammo = ammoLabel(weapon.ammoType);
  const typeLine = [
    weapon.itemTypeDisplayName,
    [weapon.damageTypeName, ammo && `${ammo} Weapon`].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(" // ");

  return (
    <div className="flex items-start gap-4">
      <div
        className={[
          "relative grid h-16 w-16 shrink-0 place-items-center border border-line-dim sm:h-20 sm:w-20",
          // Crafted weapons get the diamond frame behind the icon.
          mode === "owned-reshape" ? "before:absolute before:inset-0 before:rotate-45 before:border before:border-line-dim" : "",
        ].join(" ")}
      >
        {icon ? (
          <img src={icon} alt="" aria-hidden className="h-13 w-13 sm:h-16 sm:w-16" />
        ) : (
          <span aria-hidden className="h-13 w-13 bg-white/10 sm:h-16 sm:w-16" />
        )}
      </div>

      <div className="min-w-0">
        <p className="type-tracked text-[0.65rem] text-ink-2">
          {mode === "owned-reshape" ? "Reshape Weapon" : "Shape Weapon"}
        </p>
        <h1 className="type-display mt-1 truncate text-3xl leading-none sm:text-4xl lg:text-5xl">
          {weapon.name}
        </h1>
        {typeLine && (
          <p className="label-caps mt-1 flex items-center gap-1.5 text-sm text-ink-2">
            {/* Decorative: the element is named in the text beside it. */}
            {element && <img src={element} alt="" aria-hidden className="h-4 w-4" />}
            {typeLine}
          </p>
        )}
      </div>
    </div>
  );
}

export function FlavorText({ text }: { text?: string }) {
  if (!text) return null;
  return <p className="mt-3 max-w-xl text-sm italic text-ink-3">{text}</p>;
}
